<script lang="ts" setup>
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { toast } from 'vue-sonner';
import { useTimestamp } from '@vueuse/core';
import { AlertCircle } from 'lucide-vue-next';

import type { PricingConfig } from '@/types/organization-config';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Stepper } from '@/components/ui/stepper';
import { TagsInput } from '@/components/ui/tags-input';
import { Textarea } from '@/components/ui/textarea';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import { useRingCentralStore } from '@/stores/ringcentral';
import { trackIntegrationConnected, trackOrganizationCreated } from '@/utils/ga4';
import { sleep } from '@/utils/helpers';
import { generateTimezoneOptions } from '@/utils/timezone';

import NavTeamAddStep3Configure from './nav-team-add-step3-configure.vue';
import NavTeamAddStep4Staff from './nav-team-add-step4-staff.vue';
import { teamAddValidator } from './validators/team.validator';

// Email validation helper
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const emits = defineEmits(['close', 'preventClose']);

const ringCentralStore = useRingCentralStore();
const orgStore = useOrganizationStore();
const authStore = useAuthStore();

// OAuth window tracking
const oauthWindow = ref<Window | null>(null);
const isCheckingOAuth = ref(false);
const oauthStateId = ref<string | null>(null);
const oauthPollingInterval = ref<NodeJS.Timeout | null>(null);
const oauthStatus = ref<'PENDING' | 'SUCCESS' | 'DUPLICATE' | 'FAILED' | null>(null);

const teamAddFormSchema = toTypedSchema(teamAddValidator);

// Auto-detected values
const userTimezone = ref(Intl.DateTimeFormat().resolvedOptions().timeZone);

// Use VueUse timestamp for reactive time updates
const timestamp = useTimestamp({ offset: 0 });

// Initialize timezone options from shared utility
const timezoneOptions = ref(generateTimezoneOptions());

// Watch timestamp and update timezone options reactively to show current time
watch(timestamp, () => {
  timezoneOptions.value = generateTimezoneOptions();
});

// Franchise options
const franchiseOptions = [{ label: 'Orange Theory', value: 'orangeTheory' }] as const;

const { handleSubmit, values } = useForm({
  validationSchema: teamAddFormSchema,
  initialValues: {
    franchise: 'orangeTheory', // Required franchise
    timezone: userTimezone.value,
    description: '',
    leadEmails: [],
  },
});

// Check if user has existing organizations for pricing warning
const hasExistingStudios = computed(() => orgStore.hasOrganizations);

// Computed label for selected timezone with dynamic time
const selectedTimezoneLabel = computed(() => {
  const selectedValue = values.timezone;
  if (!selectedValue) {return '';}

  const option = timezoneOptions.value.find(opt => opt.value === selectedValue);
  return option?.label || selectedValue;
});

const isSubmitting = ref(false);
const currentStep = ref<'create' | 'oauth' | 'configure' | 'staff' | 'complete'>('create');
const createdOrganization = ref<any>(null);

// Dialog close control - emit to parent when operations are blocking
const shouldPreventClose = computed(() => {
  // Prevent close during OAuth flow
  if (oauthWindow.value || oauthPollingInterval.value) {return true;}
  // Prevent close during organization creation
  if (isSubmitting.value) {return true;}
  // Allow close in other cases
  return false;
});

// OAuth button text based on current status
const oauthButtonText = computed(() => {
  if (oauthStatus.value === 'PENDING') {
    return 'Waiting for authorization...';
  } else if (oauthStatus.value === 'SUCCESS') {
    return 'Authorization completed!';
  } else if (oauthStatus.value === 'FAILED') {
    return 'Authorization failed - Retry';
  }
  return 'I Have Completed Authorization';
});

// Watch for changes and emit to parent
watch(
  shouldPreventClose,
  prevent => {
    emits('preventClose', prevent);
  },
  { immediate: true },
);

// Prevent page refresh/close while dialog is open
// Always show warning since this is a multi-step process
function handleBeforeUnload(event: BeforeUnloadEvent) {
  const confirmationMessage =
    'You are setting up a new organization. Are you sure you want to leave?';
  event.returnValue = confirmationMessage;
  return confirmationMessage;
}

// Add beforeunload listener when component mounts
onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

// Remove beforeunload listener when component unmounts
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

// OAuth flow steps - 5 steps total
const oauthSteps = computed(() => {
  const steps = ['create', 'oauth', 'configure', 'staff', 'complete'];
  const currentIndex = steps.indexOf(currentStep.value);

  return [
    {
      id: 'create',
      title: 'Create',
      description: 'Set up your organization details',
      status: (currentIndex === 0 ? 'current' : currentIndex > 0 ? 'completed' : 'upcoming') as
        | 'completed'
        | 'current'
        | 'upcoming',
    },
    {
      id: 'oauth',
      title: 'Connect',
      description: 'Authorize RingCentral integration',
      status: (currentIndex === 1 ? 'current' : currentIndex > 1 ? 'completed' : 'upcoming') as
        | 'completed'
        | 'current'
        | 'upcoming',
    },
    {
      id: 'configure',
      title: 'Config',
      description: 'Set up pricing (optional)',
      status: (currentIndex === 2 ? 'current' : currentIndex > 2 ? 'completed' : 'upcoming') as
        | 'completed'
        | 'current'
        | 'upcoming',
    },
    {
      id: 'staff',
      title: 'Staff',
      description: 'Add team members (optional)',
      status: (currentIndex === 3 ? 'current' : currentIndex > 3 ? 'completed' : 'upcoming') as
        | 'completed'
        | 'current'
        | 'upcoming',
    },
    {
      id: 'complete',
      title: 'Complete',
      description: 'Ready to analyze calls',
      status: (currentIndex === 4 ? 'current' : 'upcoming') as 'completed' | 'current' | 'upcoming',
    },
  ];
});

// Retry OAuth process
function handleOAuthRetry() {
  // Stop any existing polling
  stopOAuthPolling();

  // Close existing OAuth window if open
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
  oauthWindow.value = null;
  oauthStateId.value = null;
  oauthStatus.value = null;

  // Restart OAuth flow
  startOAuth();
}

// Cleanup on unmount
onUnmounted(() => {
  // Stop OAuth polling
  stopOAuthPolling();

  // Close OAuth window if still open
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
});

const onSubmit = handleSubmit(async values => {
  try {
    isSubmitting.value = true;

    // Create organization in RingCentral service with new schema
    const organizationId = crypto.randomUUID();
    const organization = await ringCentralStore.createOrganization({
      name: values.name,
      franchise: values.franchise, // Required: orangeTheory
      timezone: values.timezone, // Required: IANA timezone
      description: values.description || undefined, // Optional
      domain: organizationId, // Use UUID as unique domain
      leadEmails: values.leadEmails || [], // Optional
    });

    // Store created organization for OAuth step
    createdOrganization.value = organization;

    // Track organization creation in GA4
    trackOrganizationCreated(organization.organizationId, organization.name);

    // Move to OAuth step
    currentStep.value = 'oauth';

    toast.success('Organization created successfully!', {
      position: 'top-center',
      description: `${organization.name} is ready for RingCentral integration.`,
    });

    // Wait 500ms for backend to finish processing before refreshing
    await sleep(500);

    // Refresh RingCentral organizations to include the new organization
    await ringCentralStore.getUserOrganizations(true); // Force refresh from RingCentral API

    // Also refresh user memberships from the main API
    await authStore.fetchUserMemberships(true); // Force refresh

    // Update the organization store as well
    await orgStore.loadUserOrganizations();
  } catch (error) {
    console.error('Failed to create organization:', error);
    toast.error('Failed to create organization', {
      position: 'top-center',
      description: error instanceof Error ? error.message : 'An unexpected error occurred',
    });
  } finally {
    isSubmitting.value = false;
  }
});

// Start OAuth flow in new window
async function startOAuth(accessLevel: 'account' | 'extension' = 'account') {
  if (!createdOrganization.value) {return;}

  try {
    console.log(
      '🔐 Starting OAuth flow for organization:',
      createdOrganization.value.organizationId,
      'with accessLevel:',
      accessLevel,
    );

    // Check if organization creation response includes authorizationUrl (legacy support)
    if (createdOrganization.value.authorizationUrl) {
      // Extract stateId from URL for legacy support
      const authUrl = new URL(createdOrganization.value.authorizationUrl);
      oauthStateId.value = authUrl.searchParams.get('state');

      // Open authorization URL in new window
      oauthWindow.value = window.open(
        createdOrganization.value.authorizationUrl,
        'ringcentral-oauth',
        'width=600,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no',
      );
    } else {
      // Use RingCentral store to initiate OAuth
      const result = await ringCentralStore.initiateOAuth(
        createdOrganization.value.organizationId,
        accessLevel,
      );
      oauthWindow.value = result.authWindow;
      oauthStateId.value = result.stateId;
    }

    if (!oauthWindow.value) {
      throw new Error('Unable to open authorization window. Please allow popups for this site.');
    }

    console.log('🔗 OAuth window opened, starting status polling...');
    toast.info('Complete authorization in the popup window', {
      position: 'top-center',
      description: "We'll automatically detect when authorization is complete.",
    });

    // Start polling OAuth status
    startOAuthPolling();
  } catch (error) {
    console.error('Failed to start OAuth:', error);
    toast.error('Failed to start RingCentral authorization', {
      position: 'top-center',
      description: error instanceof Error ? error.message : 'An unexpected error occurred',
    });
  }
}

// Start OAuth status polling
function startOAuthPolling() {
  if (!oauthStateId.value) {return;}

  // Set initial status to PENDING
  oauthStatus.value = 'PENDING';

  const checkStatus = async () => {
    try {
      const status = await ringCentralStore.checkOAuthStatus(oauthStateId.value!);

      // Update OAuth status for UI
      oauthStatus.value = status.status;

      if (status.status === 'PENDING') {
        // Continue polling
        console.log('📊 OAuth still pending, continuing to poll...');
      } else if (status.status === 'SUCCESS') {
        console.log('✅ OAuth completed successfully!');
        handleOAuthSuccess();
      } else if (status.status === 'DUPLICATE') {
        console.log('⚠️ OAuth duplicate account detected');
        handleOAuthDuplicate(status.providerAccountId);
      } else if (status.status === 'FAILED') {
        console.log('❌ OAuth failed');
        handleOAuthError(status.errorMessage || 'Authorization failed');
      }
    } catch (err) {
      console.error('Error checking OAuth status:', err);
      // Don't stop polling on error, might be temporary
    }
  };

  // Initial check
  checkStatus();

  // Set up polling every 2 seconds
  oauthPollingInterval.value = setInterval(checkStatus, 2000);
}

// Handle OAuth success
function handleOAuthSuccess() {
  stopOAuthPolling();

  // Close OAuth window
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
  oauthWindow.value = null;
  oauthStateId.value = null;
  oauthStatus.value = null;

  // Track integration connected in GA4 (key activation metric)
  if (createdOrganization.value?.organizationId) {
    trackIntegrationConnected(createdOrganization.value.organizationId);
  }

  // Move to configure step (not complete)
  currentStep.value = 'configure';
  toast.success('RingCentral connected successfully!', {
    position: 'top-center',
    description: 'Your organization is now connected to RingCentral.',
  });
}

// Handle OAuth duplicate account
function handleOAuthDuplicate(providerAccountId?: string) {
  stopOAuthPolling();

  // Close OAuth window
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
  oauthWindow.value = null;
  oauthStateId.value = null;
  oauthStatus.value = null;

  toast.error('Account already connected', {
    position: 'top-center',
    description: providerAccountId
      ? `RingCentral account ${providerAccountId} is already connected to another organization.`
      : 'This RingCentral account is already connected to another organization.',
  });
}

// Handle OAuth error
function handleOAuthError(errorMessage: string) {
  stopOAuthPolling();

  // Close OAuth window
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
  oauthWindow.value = null;
  oauthStateId.value = null;
  oauthStatus.value = null;

  toast.error('Authorization failed', {
    position: 'top-center',
    description: errorMessage,
  });
}

// Handle manual OAuth completion check
async function handleOAuthCompleted() {
  if (!oauthStateId.value || isCheckingOAuth.value) {return;}

  try {
    isCheckingOAuth.value = true;
    console.log('🔍 Manual OAuth status check requested...');

    const status = await ringCentralStore.checkOAuthStatus(oauthStateId.value);

    if (status.status === 'SUCCESS') {
      console.log('✅ OAuth completed successfully!');
      handleOAuthSuccess();
    } else if (status.status === 'DUPLICATE') {
      console.log('⚠️ OAuth duplicate account detected');
      handleOAuthDuplicate(status.providerAccountId);
    } else if (status.status === 'FAILED') {
      console.log('❌ OAuth failed');
      handleOAuthError(status.errorMessage || 'Authorization failed');
    } else {
      // Still pending
      toast.info('Authorization still pending', {
        position: 'top-center',
        description: 'Please complete the authorization in the popup window first.',
      });
    }
  } catch (error) {
    console.error('❌ Failed to check OAuth status:', error);
    toast.error('Failed to check authorization status', {
      position: 'top-center',
      description: error instanceof Error ? error.message : 'An unexpected error occurred',
    });
  } finally {
    isCheckingOAuth.value = false;
  }
}

// Stop OAuth polling
function stopOAuthPolling() {
  if (oauthPollingInterval.value) {
    clearInterval(oauthPollingInterval.value);
    oauthPollingInterval.value = null;
  }
}

// Skip OAuth and move to configure
function skipOAuth() {
  // Stop any existing polling
  stopOAuthPolling();

  // Clean up OAuth window if open
  if (oauthWindow.value && !oauthWindow.value.closed) {
    oauthWindow.value.close();
  }
  oauthWindow.value = null;
  oauthStateId.value = null;
  oauthStatus.value = null;

  currentStep.value = 'configure';
  toast.info('Skipped RingCentral connection', {
    position: 'top-center',
    description: 'You can connect later from Settings.',
  });
}

// Skip configure step
function skipConfigure() {
  currentStep.value = 'staff';
}

// Skip staff step
function skipStaff() {
  currentStep.value = 'complete';
}

// Configuration data storage
const pricingConfigData = ref<PricingConfig | null>(null);
const staffConfigData = ref<string | null>(null);

// Handle pricing configuration submission
async function handlePricingSubmit(data: PricingConfig) {
  console.log('💰 Pricing configuration submitted:', data);

  // Filter out empty entries before saving
  const cleanedData: PricingConfig = {
    membershipTiers: data.membershipTiers?.filter(tier => tier.name.trim() !== '') || [],
    classPacks: data.classPacks?.filter(pack => pack.name.trim() !== '') || [],
    introOffer: data.introOffer,
    addOns: data.addOns,
  };

  console.log('💰 Cleaned pricing data:', cleanedData);
  pricingConfigData.value = cleanedData;

  // TODO: Save to backend API
  // For now, just store locally and move to next step
  currentStep.value = 'staff';
}

// Clean and deduplicate staff names
function cleanStaffNames(staffString: string): string {
  if (!staffString || !staffString.trim()) {return '';}

  // Split by newline, trim each name, filter empty, deduplicate, rejoin
  const names = staffString
    .split('\n')
    .map(name => name.trim())
    .filter(name => name.length > 0);

  // Deduplicate using Set (case-insensitive)
  const uniqueNames = Array.from(new Set(names.map(name => name.toLowerCase()))).map(
    lower => names.find(name => name.toLowerCase() === lower)!,
  );

  return uniqueNames.join('\n');
}

// Handle staff configuration submission
async function handleStaffSubmit(data: string) {
  console.log('👥 Staff configuration submitted:', data);

  // Clean and deduplicate staff names
  const cleanedStaff = cleanStaffNames(data);
  console.log('👥 Cleaned staff names:', cleanedStaff);

  staffConfigData.value = cleanedStaff;

  // Save both pricing and staff config to backend
  await saveOrganizationConfig();

  // Move to complete step
  currentStep.value = 'complete';
}

// Save organization configuration to backend
async function saveOrganizationConfig() {
  if (!createdOrganization.value?.organizationId) {return;}

  try {
    isSubmitting.value = true;

    const configData = {
      pricing: pricingConfigData.value || undefined,
      staff: staffConfigData.value || undefined,
    };

    console.log('💾 Saving organization config to backend:', configData);

    // Call backend API to save config
    await ringCentralStore.updateOrganization(createdOrganization.value.organizationId, {
      businessConfig: configData,
    });

    console.log('✅ Organization config saved successfully');

    toast.success('Configuration saved successfully', {
      position: 'top-center',
      description: 'Your organization is ready to analyze calls.',
    });
  } catch (error) {
    console.error('Failed to save organization config:', error);
    toast.error('Failed to save configuration', {
      position: 'top-center',
      description: error instanceof Error ? error.message : 'An unexpected error occurred',
    });
    throw error; // Re-throw to prevent moving to complete step
  } finally {
    isSubmitting.value = false;
  }
}

// Handle dialog close with confirmation
async function handleDialogClose() {
  // If OAuth is in progress, show warning
  if (oauthWindow.value || oauthPollingInterval.value) {
    toast.warning('OAuth in progress', {
      position: 'top-center',
      description: 'Please wait for authorization to complete or retry if needed.',
    });
    return false; // Prevent closing
  }

  // If organization creation is in progress, show warning
  if (isSubmitting.value) {
    toast.warning('Organization creation in progress', {
      position: 'top-center',
      description: 'Please wait for the process to complete.',
    });
    return false; // Prevent closing
  }

  // Wait 500ms for state updates to propagate before closing
  await sleep(500);

  // Safe to close
  emits('close');
  return true;
}

const stepDescriptions = {
  create: {
    title: 'Create New Connection',
    description: 'Create a new connection for call analytics.',
  },
  oauth: {
    title: 'Connect to RingCentral',
    description: 'Authorize RingCentral integration to start analyzing your calls.',
  },
  configure: {
    title: 'Pricing Config',
    description: 'Set up pricing tiers and membership options (optional).',
  },
  staff: {
    title: 'Add Staff Members',
    description: 'Configure team members for performance tracking (optional).',
  },
  complete: {
    title: 'Setup Complete',
    description: 'Your Connection is ready!',
  },
} as Record<
  'create' | 'oauth' | 'configure' | 'staff' | 'complete',
  { title: string; description: string }
>;
</script>

<template>
  <div class="flex flex-col h-full max-h-[calc(85vh-3rem)]">
    <!-- Fixed header - doesn't scroll -->
    <div class="flex-shrink-0 border-b pb-4 mb-6 min-w-0">
      <UiDialogHeader>
        <UiDialogTitle>
          {{ stepDescriptions[currentStep].title }}
        </UiDialogTitle>
        <UiDialogDescription>
          {{ stepDescriptions[currentStep].description }}
        </UiDialogDescription>
      </UiDialogHeader>

      <!-- Progress Stepper -->
      <div class="mt-6">
        <Stepper :steps="oauthSteps" />
      </div>
    </div>

    <!-- Scrollable content area -->
    <div
      class="flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0 -mx-4 sm:-mx-6 px-4 sm:px-6 py-1"
    >
      <!-- Step 1: Create Organization Form -->
      <form v-if="currentStep === 'create'" class="space-y-6 min-w-0" @submit="onSubmit">
        <!-- Pricing Warning -->
        <Alert
          v-if="hasExistingStudios"
          class="border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-950/20"
        >
          <AlertCircle class="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <AlertDescription class="text-sm text-amber-900 dark:text-amber-100">
            <strong>Additional Organization:</strong> Each additional organization costs additional
            $299/month. Questions? Contact
            <a
              href="mailto:support@retaintive.ai"
              class="font-medium underline hover:text-amber-700 dark:hover:text-amber-300"
              >support@retaintive.ai</a
            >
          </AlertDescription>
        </Alert>

        <FormField v-slot="{ componentField }" name="name">
          <FormItem class="min-w-0">
            <FormLabel class="text-base">
              Organization Name
              <span class="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <UiInput
                v-bind="componentField"
                :disabled="isSubmitting"
                placeholder="e.g., OTF Manhattan"
                class="w-full"
              />
            </FormControl>
            <FormDescription class="break-words">
              This will be the display name for your organization.
            </FormDescription>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- Franchise selection (required) -->
        <FormField v-slot="{ componentField }" name="franchise">
          <FormItem class="min-w-0">
            <FormLabel class="text-base"> Franchise <span class="text-red-500">*</span> </FormLabel>
            <FormControl>
              <Select v-bind="componentField" :disabled="isSubmitting">
                <SelectTrigger>
                  <SelectValue placeholder="Select franchise" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="option in franchiseOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormDescription class="break-words">
              Currently only Orange Theory franchise is supported.
            </FormDescription>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="timezone">
          <FormItem class="min-w-0">
            <FormLabel class="text-base"> Timezone <span class="text-red-500">*</span> </FormLabel>
            <FormControl>
              <Select v-bind="componentField" :disabled="isSubmitting">
                <SelectTrigger class="w-full">
                  <SelectValue placeholder="Select timezone" class="truncate">
                    <span class="truncate">{{ selectedTimezoneLabel || 'Select timezone' }}</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="option in timezoneOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormDescription class="break-words">
              <span v-if="timezoneOptions.find(opt => opt.value === userTimezone)">
                Your current timezone:
                {{ timezoneOptions.find(opt => opt.value === userTimezone)?.label }}
              </span>
              <span v-else> Your timezone: {{ userTimezone }} (not in US main timezones) </span>
            </FormDescription>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- Description (optional) -->
        <FormField v-slot="{ componentField }" name="description">
          <FormItem class="min-w-0">
            <FormLabel class="text-base"> Description </FormLabel>
            <FormControl>
              <Textarea
                v-bind="componentField"
                placeholder="My second studios at 5th Avenue..."
                :disabled="isSubmitting"
                class="resize-none"
                rows="3"
              />
            </FormControl>
            <FormDescription class="break-words">
              (Optional) Description for this organization (max 1000 characters)
            </FormDescription>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- Lead Emails (optional) -->
        <FormField v-slot="{ value, handleChange }" name="leadEmails">
          <FormItem>
            <FormLabel class="text-base"> Lead Emails </FormLabel>
            <FormControl>
              <TagsInput
                :model-value="value"
                placeholder="Add lead emails (e.g., manager@example.com)..."
                :disabled="isSubmitting"
                :validate-tag="isValidEmail"
                @update:model-value="handleChange"
              />
            </FormControl>
            <FormDescription class="break-words">
              (Optional) Email addresses for leads associated with this organization. Must be valid
              email format.
            </FormDescription>
            <FormMessage />
          </FormItem>
        </FormField>

        <!-- Show error if exists -->
        <div v-if="ringCentralStore.error" class="rounded-lg bg-red-50 border border-red-200 p-3">
          <div class="flex items-start">
            <div class="flex-1">
              <p class="text-sm text-red-800">
                {{ ringCentralStore.error }}
              </p>
            </div>
          </div>
        </div>

        <div class="flex justify-between mt-8 pt-4 border-t border-border">
          <UiButton
            type="button"
            variant="outline"
            :disabled="isSubmitting"
            @click="handleDialogClose"
          >
            Cancel
          </UiButton>
          <UiButton type="submit" :loading="isSubmitting" :disabled="isSubmitting">
            <template v-if="isSubmitting"> Creating... </template>
            <template v-else> Create </template>
          </UiButton>
        </div>
      </form>

      <!-- Step 2: OAuth Authorization -->
      <div v-else-if="currentStep === 'oauth'" class="space-y-4">
        <div class="text-center py-6">
          <div
            class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 mb-4"
          >
            <svg class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-foreground mb-2">Connect to RingCentral</h3>
          <p class="text-sm text-muted-foreground mb-2">
            Your organization "{{ createdOrganization?.name }}" has been created successfully.
          </p>
          <p class="text-sm text-muted-foreground mb-6">
            Now connect it to RingCentral to start analyzing your calls.
          </p>

          <div class="flex flex-col gap-3">
            <!-- Initial OAuth Button -->
            <!-- Two OAuth buttons -->
            <div v-if="!oauthWindow" class="flex flex-col gap-2">
              <UiButton class="w-full" @click="startOAuth('account')">
                <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                  />
                </svg>
                Account Level (Recommended)
              </UiButton>
              <UiButton class="w-full" @click="startOAuth('extension')">
                <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                  />
                </svg>
                Extension Level
              </UiButton>
              <p class="text-xs text-gray-500 mt-1">
                Need all calls? Choose account-level. Need calls from just one extension? Choose
                extension-level.
              </p>
            </div>

            <!-- Post-OAuth Action Buttons -->
            <div v-else class="flex flex-col gap-2">
              <UiButton
                class="w-full"
                :loading="isCheckingOAuth || oauthStatus === 'PENDING'"
                :variant="
                  oauthStatus === 'SUCCESS'
                    ? 'default'
                    : oauthStatus === 'FAILED'
                      ? 'destructive'
                      : 'default'
                "
                @click="handleOAuthCompleted"
              >
                <svg
                  v-if="oauthStatus === 'SUCCESS'"
                  class="w-4 h-4 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
                <svg
                  v-else-if="oauthStatus === 'FAILED'"
                  class="w-4 h-4 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
                  />
                </svg>
                <svg
                  v-else-if="!isCheckingOAuth && oauthStatus !== 'PENDING'"
                  class="w-4 h-4 mr-2"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
                {{ isCheckingOAuth ? 'Checking...' : oauthButtonText }}
              </UiButton>
              <UiButton variant="outline" class="w-full" @click="handleOAuthRetry">
                <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"
                  />
                </svg>
                Retry Authorization
              </UiButton>
            </div>

            <UiButton variant="outline" class="w-full" @click="skipOAuth"> Skip for Now </UiButton>
          </div>
        </div>
      </div>

      <!-- Step 3: Pricing Config -->
      <NavTeamAddStep3Configure
        v-else-if="currentStep === 'configure'"
        :is-submitting="isSubmitting"
        @submit="handlePricingSubmit"
        @skip="skipConfigure"
      />

      <!-- Step 4: Add Staff -->
      <NavTeamAddStep4Staff
        v-else-if="currentStep === 'staff'"
        :is-submitting="isSubmitting"
        @submit="handleStaffSubmit"
        @skip="skipStaff"
      />

      <!-- Step 5: Setup Complete -->
      <div v-else-if="currentStep === 'complete'" class="space-y-4">
        <div class="text-center py-6">
          <div
            class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4"
          >
            <svg
              class="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 class="text-lg font-medium text-foreground mb-2">Setup Complete!</h3>
          <p class="text-sm text-muted-foreground mb-6">
            Your organization "{{ createdOrganization?.name }}" is ready. You can now start managing
            and analyzing your calls.
          </p>

          <UiButton class="w-full" @click="emits('close')"> Get Started </UiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
